import React from 'react';
import { motion } from 'motion/react';
import { CheckCircle2, ArrowRight, HelpCircle, ShieldCheck } from 'lucide-react';
import { SERVICES, FAQS } from '@/src/constants';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import ComparisonTable from '@/src/components/ComparisonTable';
import SEO from '@/src/components/SEO';
import { servicesSchema } from '@/src/lib/schema';

export default function Services() {
  return (
    <div className="flex flex-col">
      <SEO 
        title="Cybersecurity Services – Risk Audits & Compliance | AGRANDINTL"
        description="Expert cybersecurity services tailored for retail and cloud businesses. From risk audits to penetration testing and PCI compliance support in DC, VA, and MD."
        keywords="cybersecurity risk audit, pci compliance, vulnerability assessment DC, cybersecurity consulting maryland, network security virginia"
        schema={servicesSchema}
      />
      {/* Page Header */}
      <section className="py-20 bg-secondary/[0.01] relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[9px] font-bold uppercase tracking-[0.3em] mb-8"
            >
              Strategic Consulting Framework
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl md:text-5xl lg:text-6xl font-display font-bold mb-8 tracking-tight leading-tight"
            >
              Expert <br />
              <span className="text-accent">Solutions</span>
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-base md:text-lg text-muted-foreground leading-relaxed font-normal max-w-2xl"
            >
              Professional security services designed to protect your business from modern threats.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="py-20">
        <div className="container mx-auto px-4 md:px-8 space-y-20">
          {/* Featured Highlight for Risk Audit */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-8 border-accent/20 bg-accent/[0.02] flex flex-col md:flex-row items-center justify-between gap-8 mb-20"
          >
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-accent" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-accent">Recommended Starting Point</span>
              </div>
              <h2 className="text-2xl font-display font-bold tracking-tight">Professional Cybersecurity Risk Audit</h2>
              <p className="text-sm text-muted-foreground max-w-xl leading-relaxed">
                A high-impact baseline for your security posture. We identify your top risks and provide a clear roadmap to fix them.
              </p>
            </div>
            <div className="flex flex-col items-center md:items-end gap-3 shrink-0">
              <div className="text-center md:text-right">
                <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Fixed Investment</span>
                <p className="text-4xl font-display font-bold text-accent tracking-tighter">$299<span className="text-xs text-muted-foreground ml-1">/ audit</span></p>
              </div>
              <Button asChild size="lg" className="px-8 h-12 rounded-xl font-bold text-xs shadow-lg shadow-accent/10">
                <Link to="/contact?service=audit">Order Your Audit</Link>
              </Button>
            </div>
          </motion.div>

          {SERVICES.map((service, index) => (
            <div 
              key={service.id} 
              id={service.id}
              className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
            >
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`space-y-6 ${index % 2 === 1 ? 'lg:order-2' : ''}`}
              >
                <div className="w-9 h-9 rounded-xl bg-accent/5 border border-accent/10 flex items-center justify-center text-accent">
                  <service.icon className="w-4.5 h-4.5" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-xl md:text-3xl font-display font-bold tracking-tight leading-tight">{service.title}</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed font-normal">
                    {service.details}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <h3 className="font-bold font-display text-foreground text-[9px] tracking-widest uppercase">Key Business Benefits</h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {service.benefits.map((benefit) => (
                      <li 
                        key={benefit} 
                        className="flex items-start gap-2 text-xs text-muted-foreground font-normal"
                      >
                        <CheckCircle2 className="w-3 h-3 text-accent shrink-0 mt-0.5" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>

                <Button asChild variant="outline" className="border-accent/10 hover:bg-accent hover:text-background rounded-xl h-10 px-5 font-bold text-xs transition-all">
                  <Link to="/contact">Inquire About This Service</Link>
                </Button>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, scale: 0.98 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''} group`}
              >
                <div className="relative glass-card rounded-2xl overflow-hidden aspect-video flex items-center justify-center bg-secondary/[0.02] border-white/5 group-hover:border-accent/10 transition-all duration-500">
                  <service.icon className="w-12 h-12 text-accent/5 group-hover:text-accent/10 transition-colors" />
                  <div className="absolute bottom-3 left-3 right-3 p-3 glass-card rounded-xl border-white/5">
                    <p className="text-[7px] font-bold font-display text-accent mb-0.5 uppercase tracking-[0.1em]">Service Focus</p>
                    <p className="text-sm font-bold font-display tracking-tight">{service.title}</p>
                  </div>
                </div>
              </motion.div>
            </div>
          ))}
        </div>
      </section>

      {/* Methodology Section */}
      <section className="py-20 relative overflow-hidden bg-secondary/[0.01]">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center mb-12 space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[8px] font-bold uppercase tracking-[0.2em]">
              Operational Excellence
            </div>
            <h2 className="text-2xl md:text-4xl font-display font-bold tracking-tight">Our <span className="text-accent">Process</span></h2>
            <p className="text-muted-foreground max-w-2xl mx-auto text-base font-normal leading-relaxed">
              A clear, professional approach to securing your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { 
                step: "01", 
                title: "Discovery", 
                desc: "We learn about your business, your systems, and your specific security concerns." 
              },
              { 
                step: "02", 
                title: "Security Audit", 
                desc: "A thorough check of your technical settings and business processes." 
              },
              { 
                step: "03", 
                title: "Hardening", 
                desc: "Implementing the security fixes and monitoring needed to keep you safe." 
              }
            ].map((item, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="space-y-2 group cursor-default"
              >
                <div className="text-4xl font-display font-black text-accent/5 group-hover:text-accent/10 transition-colors duration-500 leading-none">{item.step}</div>
                <h3 className="text-base font-display font-bold tracking-tight group-hover:text-accent transition-colors">{item.title}</h3>
                <p className="text-muted-foreground leading-relaxed text-xs font-normal">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Assessment CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8 relative z-10">
          <div className="glass-card rounded-2xl p-10 md:p-12 text-center space-y-4 max-w-3xl mx-auto overflow-hidden relative">
            <div className="relative z-10 space-y-4">
              <h2 className="text-2xl md:text-4xl font-display font-bold tracking-tight leading-tight">
                Measure Your <br />
                <span className="text-accent">Security Posture</span>
              </h2>
              <p className="text-sm text-muted-foreground max-w-lg mx-auto font-normal leading-relaxed">
                Take our interactive assessment to receive a personalized analysis of your current security gaps and strategic recommendations.
              </p>
              <Button asChild size="lg" className="font-bold px-6 h-11 rounded-xl text-xs">
                <Link to="/assessment">Launch Posture Audit</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing & Commitment */}
      <ComparisonTable />

      {/* FAQ Section */}
      <section className="py-20 bg-background relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[8px] font-bold uppercase tracking-[0.2em]">
                Common Inquiries
              </div>
              <h2 className="text-2xl md:text-4xl font-display font-bold tracking-tight leading-tight">
                Service <br />
                <span className="text-accent">Intelligence</span>
              </h2>
              <p className="text-sm text-muted-foreground font-normal leading-relaxed max-w-md">
                Detailed answers to common questions about our engagement models and methodologies.
              </p>
              <div className="pt-2">
                <div className="p-4 rounded-xl bg-accent/5 border border-accent/10 flex items-center gap-4 group">
                  <div className="w-8 h-8 rounded-xl bg-accent flex items-center justify-center text-background shrink-0">
                    <HelpCircle className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-bold text-xs">Still have questions?</p>
                    <Link to="/contact" className="text-accent text-[9px] font-bold hover:underline uppercase tracking-wider">Speak with an Advisor</Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <Accordion className="w-full space-y-2">
                {FAQS.map((faq, index) => (
                  <AccordionItem 
                    key={index} 
                    value={`item-${index}`}
                    className="border border-white/5 rounded-xl px-4 bg-secondary/[0.01] hover:bg-secondary/[0.03] transition-colors overflow-hidden"
                  >
                    <AccordionTrigger className="text-left py-3.5 hover:no-underline hover:text-accent font-bold text-xs tracking-tight">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-[11px] leading-relaxed pb-3.5 font-normal">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-secondary/[0.03]">
        <div className="container mx-auto px-4 md:px-6 text-center space-y-4">
          <h2 className="text-2xl md:text-4xl font-display font-bold">Need a Custom Security Strategy?</h2>
          <p className="text-muted-foreground max-w-xl mx-auto text-base font-normal">
            Every business is unique. We can combine our services to create a comprehensive security posture that fits your specific operational needs.
          </p>
          <Button asChild size="lg" className="font-bold px-8 h-12 rounded-xl text-sm">
            <Link to="/contact">Schedule a Strategy Session</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
